import { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import apiService from '@/service/apiService';
import { useAppSelector } from '@/redux/store';
import { capitalizeFirstLetter } from '@/utils';

interface useLoadExistBillCheckProps {
  documentNumber: string;
  initialData?: any;
  onDocumentCheckSuccess?: (response: any) => void;
  onDocumentCheckError?: (error: any) => void;
  onDocumentNotFound?: () => void;
}

interface LoadExistBillCheckResult {
  isAvailable: null | boolean;
  isLoading: boolean;
  loadStatusError?: string;
  checkDocumentNumber: () => Promise<void>;
}

export const useLoadExistBillCheck = ({
  documentNumber,
  initialData,
  onDocumentCheckSuccess,
  onDocumentCheckError,
  onDocumentNotFound,
}: useLoadExistBillCheckProps): LoadExistBillCheckResult => {
  const form = useFormContext();
  const [isLoading, setIsLoading] = useState(false);
   const [isAvailable, setIsAvailable] = useState<null | boolean>(null);
  const [loadStatusError, setLoadStatusError] = useState<string>();
  const resetFormFields = useCallback(() => {
      form.setValue("expense", []);
      form.setValue("loadId", "");
      form.setValue("loadAmount", 0);
      form.setValue("vendorId", "");
  }, [form]);

  const checkDocumentNumber = useCallback(async () => {
    if (!documentNumber?.trim()) return;

    try {
      setIsLoading(true);
      setLoadStatusError(undefined);

      const response = await apiService.checkLoadNumberExist(documentNumber);

      // Check if load exists and status is not delivered
      if (response?.load) {
        const loadData = response.load;
        // Load is delivered, proceed with normal flow
          const carrierData = loadData?.carrierIds?.carrier;
          form.setValue("loadId", loadData?._id || "");
          form.setValue("loadAmount", loadData?.loadAmount);
          form.setValue("deletedfiles", []);
          form.setValue("vendorId", carrierData?._id || "");
          form.clearErrors('loadNumber'); // Clear error only on successful validation
      } else {
        // No load found
        onDocumentNotFound?.();
        resetFormFields();
      }
      // Handle expenses
      if (response?.expenses?.length > 0 && !initialData?._id && !form.watch("expense")?.length) {
        form.setValue("expense", response.expenses);
      }
      if (response?.expenses?.length == 0 && !initialData?._id && !form.watch("expense")?.length) {
        form.setValue("expense", []);
      }

      setIsAvailable(true);
      onDocumentCheckSuccess?.(response);

    } catch (error:any) {
      const errorMessage =error?.message  || "Something IS Wrong"
      form.setError('loadNumber', {
        type: 'manual',
        message: errorMessage
      });
      setIsAvailable(false);
      resetFormFields();
      onDocumentCheckError?.(error);
    } finally {
      setIsLoading(false);
    }
  }, [documentNumber, initialData, onDocumentCheckSuccess, onDocumentCheckError, onDocumentNotFound, resetFormFields]);

  // Auto-check when document number changes (only for new documents)
  useEffect(() => {
    if (!initialData?._id) {
      // Clear error only if load number is empty AND there's no existing error
      if (!documentNumber?.trim() && form.formState.errors.loadNumber) {
        form.clearErrors('loadNumber');
        return;
      }
      
      const timeoutId = setTimeout(() => {
        checkDocumentNumber();
      }, 500); // Debounce for 500ms

      return () => clearTimeout(timeoutId);
    }
  }, [documentNumber]);

  // Set availability when editing
  useEffect(() => {
    if (initialData?._id) {
      setIsAvailable(true);
      setLoadStatusError(undefined);
    }
  }, [initialData?._id]);

  return {
    isLoading,
    loadStatusError,
    isAvailable,
    checkDocumentNumber
  };
};
